import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    GlobalStyles,
    Grid,
    InputAdornment,
    InputLabel,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { ArrowBack, Print, Download, Search } from '@mui/icons-material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    LabelList,
    Line,
    ResponsiveContainer,
    Scatter,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import CodeChip from '../coding/CodeChip';
import { SPEAKER_COLORS } from '../coding/codingColors';
import { NotFound } from '../layout/SystemState';
import { exportLessonCsv, getLesson, retryAnalysis } from '../../services/lessonService';
import { saveFile } from '../../services/download';
import { ResearchUseChip } from './MyLessons';
import { RC_COLORS, RC_NAMES, RC_ORDER, SPEAKER_SERIES, UT_NAMES, summarize } from './reportStats';
import { secondsFor } from './transcriptCheck';

const POLL_MS = 5000;
const INK = '#4B5565';
const GRID = 'rgba(11, 18, 32, 0.08)';
const shortDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Charts measure their box on screen and keep that width when the page re-flows for paper, so while
// printing they are drawn at this width, without animation, to fit an A4 or Letter page
const PRINT_WIDTH = 640;
// Left inset of the axis labels inside the chart
const LABEL_INSET = 4;
// The two charts side by side on a wide screen stack on paper
const PRINT_FULL = { '@media print': { flexBasis: '100%', maxWidth: '100%' } };

// While printing: no navbar, white page, colours kept, cards not split across pages
const printStyles = (
    <GlobalStyles
        styles={{
            '@media print': {
                '@page': { margin: '12mm' },
                'header.MuiAppBar-root': { display: 'none' },
                body: { backgroundColor: '#FFFFFF', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' },
            },
        }}
    />
);

// The browser's print, from the button or Ctrl+P: flushSync redraws the charts before the page is laid out
const usePrinting = () => {
    const [printing, setPrinting] = useState(false);
    useEffect(() => {
        const before = () => flushSync(() => setPrinting(true));
        const after = () => setPrinting(false);
        window.addEventListener('beforeprint', before);
        window.addEventListener('afterprint', after);
        return () => {
            window.removeEventListener('beforeprint', before);
            window.removeEventListener('afterprint', after);
        };
    }, []);
    return printing;
};

const StatCard = ({ label, value, caption }) => (
    <Paper sx={{ p: 2.5, height: '100%', breakInside: 'avoid' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography sx={{ fontFamily: (t) => t.typography.h1.fontFamily, fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.2, my: 0.5 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{caption}</Typography>
    </Paper>
);

const ChartCard = ({ title, subtitle, legend, children }) => (
    <Paper component="section" aria-label={title} sx={{ p: { xs: 2, sm: 3 }, height: '100%', breakInside: 'avoid' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            <Box>
                <Typography variant="h5" component="h2">{title}</Typography>
                <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
            </Box>
            {legend}
        </Box>
        {children}
    </Paper>
);

const LegendItem = ({ color, label, line }) => (
    <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, listStyle: 'none' }}>
        <Box aria-hidden sx={line ? { width: 16, height: 2, bgcolor: color } : { width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
        <Typography variant="caption" sx={{ color: 'text.primary' }}>{label}</Typography>
    </Box>
);

const Legend = ({ items }) => (
    <Box component="ul" aria-label="Legend" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', m: 0, p: 0 }}>
        {items.map((i) => <LegendItem key={i.label} {...i} />)}
    </Box>
);

const TooltipBox = ({ children }) => (
    <Paper sx={{ px: 1.5, py: 1, boxShadow: '0 4px 12px rgba(11,18,32,0.12)' }}>{children}</Paper>
);

// Utterance types: one bar per code, coloured by who says it (teacher, student, O for both)
const UtChart = ({ data, onPick, printing }) => (
    <ResponsiveContainer width={printing ? PRINT_WIDTH : '100%'} height={data.length * 30 + 20}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 36, left: 0, bottom: 0 }} barCategoryGap={6}>
            <XAxis type="number" hide />
            <YAxis
                type="category"
                dataKey="code"
                width={190}
                tickLine={false}
                axisLine={false}
                // Labels start at the chart's left edge (the axis has no left margin), not back from the tick
                tick={({ y, payload }) => (
                    <g transform={`translate(${LABEL_INSET},${y})`}>
                        <text x={0} y={4} fontSize={12} fontWeight={700} fill="#0B1220">{payload.value}</text>
                        <text x={34} y={4} fontSize={12} fill={INK}>{UT_NAMES[payload.value]}</text>
                    </g>
                )}
            />
            <Tooltip
                cursor={{ fill: 'rgba(30,58,95,0.06)' }}
                content={({ active, payload }) => active && payload?.length ? (
                    <TooltipBox><Typography variant="body2"><strong>{payload[0].payload.code}</strong> {payload[0].payload.name}: {payload[0].value}</Typography></TooltipBox>
                ) : null}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} isAnimationActive={!printing} onClick={(d) => onPick({ ut: d.code })} style={{ cursor: 'pointer' }}>
                {data.map((d) => <Cell key={d.code} fill={SPEAKER_SERIES[d.speaker]} />)}
                <LabelList dataKey="count" position="right" fontSize={12} fill="#0B1220" />
            </Bar>
        </BarChart>
    </ResponsiveContainer>
);

// RC by speaker: each speaker's utterances split into the 5 classes, as a share
const RcChart = ({ data, onPick, printing }) => (
    <ResponsiveContainer width={printing ? PRINT_WIDTH : '100%'} height={160}>
        <BarChart data={data} layout="vertical" stackOffset="expand" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barCategoryGap={18}>
            <XAxis type="number" hide domain={[0, 1]} />
            <YAxis
                type="category"
                dataKey="speaker"
                width={84}
                tickLine={false}
                axisLine={false}
                tick={({ y, payload, index }) => (
                    <g transform={`translate(${LABEL_INSET},${y})`}>
                        <text x={0} y={-2} fontSize={13} fontWeight={700} fill="#0B1220">{payload.value}</text>
                        <text x={0} y={14} fontSize={11} fill={INK}>{data[index]?.count} utt.</text>
                    </g>
                )}
            />
            <Tooltip
                cursor={false}
                content={({ active, payload }) => active && payload?.length ? (
                    <TooltipBox>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{payload[0].payload.speaker}</Typography>
                        {payload.map((p) => <Typography key={p.dataKey} variant="caption" component="p">{p.dataKey} {RC_NAMES[p.dataKey]}: {Math.round(p.payload[p.dataKey])}%</Typography>)}
                    </TooltipBox>
                ) : null}
            />
            {RC_ORDER.map((code) => (
                <Bar key={code} dataKey={code} stackId="rc" isAnimationActive={!printing} fill={RC_COLORS[code]} stroke="#FFFFFF" strokeWidth={2} onClick={(d) => onPick({ speaker: d.role, rc: code })} style={{ cursor: 'pointer' }}>
                    <LabelList dataKey={code} position="center" fontSize={11} fill="#0B1220" formatter={(v) => (v >= 7 ? `${Math.round(v)}%` : '')} />
                </Bar>
            ))}
        </BarChart>
    </ResponsiveContainer>
);

// The lesson's CCI utterance by utterance, with a 10-utterance rolling average
const Timeline = ({ data, printing }) => (
    <ResponsiveContainer width={printing ? PRINT_WIDTH : '100%'} height={236}>
        <ComposedChart data={data} margin={{ top: 8, right: 12, left: -18, bottom: 16 }}>
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis dataKey="index" type="number" domain={[1, 'dataMax']} tick={{ fontSize: 11, fill: INK }} tickLine={false} axisLine={{ stroke: GRID }} label={{ value: 'Utterance', position: 'insideBottomRight', offset: -12, fontSize: 11, fill: INK }} />
            <YAxis domain={[0, 3]} ticks={[0, 1, 2, 3]} tick={{ fontSize: 11, fill: INK }} tickLine={false} axisLine={false} />
            <Tooltip
                content={({ active, payload }) => active && payload?.length ? (
                    <TooltipBox>
                        <Typography variant="caption" component="p"><strong>#{payload[0].payload.number}</strong> · {payload[0].payload.role === 'teacher' ? 'Teacher' : 'Student'} · {payload[0].payload.rc} (CCI {payload[0].payload.cci})</Typography>
                        <Typography variant="caption" component="p" color="text.secondary">Rolling average {payload[0].payload.rolling}</Typography>
                    </TooltipBox>
                ) : null}
            />
            <Scatter name="Teacher" data={data.filter((d) => d.role === 'teacher')} dataKey="cci" fill={SPEAKER_SERIES.teacher} shape={(p) => <circle cx={p.cx} cy={p.cy} r={3.5} fill={SPEAKER_SERIES.teacher} />} isAnimationActive={false} />
            <Scatter name="Student" data={data.filter((d) => d.role === 'student')} dataKey="cci" fill={SPEAKER_SERIES.student} shape={(p) => <circle cx={p.cx} cy={p.cy} r={3.5} fill={SPEAKER_SERIES.student} />} isAnimationActive={false} />
            <Line dataKey="rolling" stroke="#0B1220" strokeWidth={2} dot={false} isAnimationActive={false} />
        </ComposedChart>
    </ResponsiveContainer>
);

const SPEAKER_LABEL = { teacher: 'Teacher', student: 'Student' };

// The utterances, filtered in the browser from the dropdowns or a click on a chart (frame 2)
const UtteranceTable = ({ utterances, filter, setFilter }) => {
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    useEffect(() => setPage(0), [filter]);

    const rows = useMemo(() => {
        const q = filter.q.trim().toLowerCase();
        return utterances.filter((u) => (!filter.speaker || u.role === filter.speaker)
            && (!filter.ut || u.ut === filter.ut)
            && (!filter.rc || u.rc === filter.rc)
            && (!q || u.text.toLowerCase().includes(q)));
    }, [utterances, filter]);

    const chips = [
        filter.speaker && { key: 'speaker', label: SPEAKER_LABEL[filter.speaker] },
        filter.ut && { key: 'ut', label: `UT ${filter.ut}` },
        filter.rc && { key: 'rc', label: `RC ${filter.rc}` },
    ].filter(Boolean);
    const set = (key) => (e) => setFilter((f) => ({ ...f, [key]: e.target.value }));

    return (
        <Paper component="section" aria-label="Utterances" sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" component="h2">Utterances</Typography>
                <Typography variant="body2" color="text.secondary" aria-live="polite">
                    {rows.length === utterances.length ? `${utterances.length} utterances` : `${rows.length} of ${utterances.length} utterances`}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                {[['speaker', 'Speaker', [['teacher', 'Teacher'], ['student', 'Student']]],
                    ['ut', 'UT', Object.keys(UT_NAMES).map((c) => [c, `${c} · ${UT_NAMES[c]}`])],
                    ['rc', 'RC', RC_ORDER.map((c) => [c, `${c} · ${RC_NAMES[c]}`])]].map(([key, label, options]) => (
                    <FormControl key={key} size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id={`f-${key}`}>{label}</InputLabel>
                        <Select labelId={`f-${key}`} label={label} value={filter[key]} onChange={set(key)}>
                            <MenuItem value="">All</MenuItem>
                            {options.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                        </Select>
                    </FormControl>
                ))}
                <TextField
                    size="small"
                    placeholder="Search the transcript"
                    value={filter.q}
                    onChange={set('q')}
                    inputProps={{ 'aria-label': 'Search the transcript' }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    sx={{ flex: '1 1 220px' }}
                />
            </Box>
            {chips.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Filtered:</Typography>
                    {chips.map((c) => <Chip key={c.key} size="small" label={c.label} onDelete={() => setFilter((f) => ({ ...f, [c.key]: '' }))} />)}
                    <Button size="small" onClick={() => setFilter({ speaker: '', ut: '', rc: '', q: filter.q })}>Clear all</Button>
                </Box>
            )}
            <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
                <Table size="small" aria-label="Utterances table" sx={{ minWidth: 560 }}>
                    <TableHead>
                        <TableRow><TableCell sx={{ width: 56 }}>#</TableCell><TableCell sx={{ width: 90 }}>Speaker</TableCell><TableCell>Utterance</TableCell><TableCell sx={{ width: 64 }}>UT</TableCell><TableCell sx={{ width: 64 }}>RC</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.slice(page * perPage, page * perPage + perPage).map((u) => (
                            <TableRow key={u.number}>
                                <TableCell sx={{ color: 'text.secondary' }}>{u.number}</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: SPEAKER_COLORS[u.role] }}>{SPEAKER_LABEL[u.role]}</TableCell>
                                <TableCell>{u.text}</TableCell>
                                <TableCell><CodeChip code={u.ut} coded title={UT_NAMES[u.ut]} /></TableCell>
                                <TableCell><CodeChip code={u.rc} coded title={RC_NAMES[u.rc]} /></TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>No utterances match these filters.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
            />
        </Paper>
    );
};

// Frame 3: still analysing, or failed; the same story as the upload page
const NotReady = ({ lesson, onRetry, retrying }) => {
    const total = secondsFor(lesson.utteranceCount);
    const elapsed = lesson.analysisStartedAt ? Math.round((Date.now() - new Date(lesson.analysisStartedAt).getTime()) / 1000) : 0;
    const left = Math.max(0, total - elapsed);
    return lesson.status === 'analysing' ? (
        <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 640 }} aria-live="polite">
            <Typography variant="h4" component="h2">This lesson is still being analysed</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Coding {lesson.utteranceCount} utterances · {left > 0 ? `about ${left} seconds left` : 'Almost done'}
            </Typography>
            <LinearProgress variant={left > 0 ? 'determinate' : 'indeterminate'} value={Math.min(96, (elapsed / total) * 100)} sx={{ mt: 2, height: 8, borderRadius: 999 }} aria-label="Analysis progress" />
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>
                The report appears here when it is done. If the analysis fails, this card says so and offers Try again, as on the upload page.
            </Typography>
        </Paper>
    ) : (
        <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 640 }}>
            <Typography variant="h4" component="h2">The analysis did not finish</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The coding service did not answer. Your lesson and your research-use choice were saved; nothing else is needed from you.
            </Typography>
            <Button variant="contained" onClick={onRetry} disabled={retrying} sx={{ mt: 3 }}>Try again</Button>
        </Paper>
    );
};

// /lessons/:lessonId (wf-teacher-analysis 1–3, YES-266): one scrolling report, read top to bottom.
// No confidence anywhere: not in the table, as shading or icons, as a sort order, or in the CSV.
const LessonReport = () => {
    const { lessonId } = useParams();
    const [lesson, setLesson] = useState(null);
    const [status, setStatus] = useState('loading');
    const [retrying, setRetrying] = useState(false);
    const [filter, setFilter] = useState({ speaker: '', ut: '', rc: '', q: '' });
    const [notice, setNotice] = useState(null);
    const printing = usePrinting();

    const load = useCallback(async () => {
        try {
            setLesson(await getLesson(lessonId));
            setStatus('ready');
        } catch (err) {
            setStatus(err.response?.status === 404 ? 'notFound' : 'error');
        }
    }, [lessonId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (lesson?.status !== 'analysing') return undefined;
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [lesson?.status, load]);

    const stats = useMemo(() => (lesson?.utterances ? summarize(lesson.utterances) : null), [lesson]);

    const pick = (next) => {
        setFilter((f) => ({ ...f, speaker: '', ut: '', rc: '', ...next }));
        document.getElementById('utterances')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const retry = async () => {
        setRetrying(true);
        try {
            await retryAnalysis(lessonId);
        } catch (err) {
            // 409: ready or analysing meanwhile; the reload shows which
        } finally {
            setRetrying(false);
            load();
        }
    };

    const download = async () => {
        try {
            const { blob, fileName } = await exportLessonCsv(lessonId);
            saveFile(blob, fileName || `${(lesson.name || lesson.filename).replace(/\.csv$/i, '')}_codes.csv`);
        } catch (err) {
            setNotice('Couldn’t download the CSV. Try again.');
        }
    };

    if (status === 'notFound') return <NotFound />;
    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load this lesson.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto', '@media print': { '& .no-print': { display: 'none' } } }}>
            {printStyles}
            <Link component={RouterLink} to="/dashboard" underline="hover" className="no-print" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}>
                <ArrowBack sx={{ fontSize: 16 }} /> My lessons
            </Link>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'flex-end' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mt: 0.5, mb: 3 }}>
                <Box>
                    <Typography variant="h2" component="h1">{lesson ? lesson.name || lesson.filename : <Skeleton width={320} />}</Typography>
                    {lesson && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">Uploaded {shortDate(lesson.uploadedAt)} · {lesson.filename} · {lesson.utteranceCount} utterances</Typography>
                            <Box component={RouterLink} to="/dashboard" sx={{ textDecoration: 'none' }} aria-label="Research use, on My lessons">
                                <ResearchUseChip shared={lesson.researchUse} />
                            </Box>
                        </Box>
                    )}
                </Box>
                {stats && (
                    <Box className="no-print" sx={{ display: 'flex', gap: 1.5 }}>
                        <Button variant="outlined" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
                        <Button variant="outlined" startIcon={<Download />} onClick={download}>Download CSV</Button>
                    </Box>
                )}
            </Box>
            {notice && <Alert severity="error" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}

            {!lesson && <Skeleton variant="rounded" height={400} />}
            {lesson && !stats && <NotReady lesson={lesson} onRetry={retry} retrying={retrying} />}

            {stats && (
                <Box sx={{ display: 'grid', gap: 2.5 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md><StatCard label="Utterances" value={stats.total} caption="in this lesson" /></Grid>
                        <Grid item xs={6} md><StatCard label="Teacher talk" value={`${stats.teacherShare}%`} caption={`${stats.teacher} utterances`} /></Grid>
                        <Grid item xs={6} md><StatCard label="Student talk" value={`${100 - stats.teacherShare}%`} caption={`${stats.student} utterances`} /></Grid>
                        <Grid item xs={6} md><StatCard label="Responses per question" value={stats.responsesPerQuestion ?? '—'} caption="per teacher question" /></Grid>
                        <Grid item xs={12} md><StatCard label="Average CCI" value={stats.cci.lesson.toFixed(1)} caption="reasoning complexity, 0 to 3" /></Grid>
                    </Grid>

                    <Grid container spacing={2.5}>
                        <Grid item xs={12} lg={6} sx={PRINT_FULL}>
                            <ChartCard
                                title="Utterance types (UT)"
                                subtitle="Number of utterances with each code"
                                legend={<Legend items={[{ color: SPEAKER_SERIES.teacher, label: 'Teacher codes' }, { color: SPEAKER_SERIES.student, label: 'Student codes' }, { color: SPEAKER_SERIES.other, label: 'Either' }]} />}
                            >
                                <UtChart data={stats.ut} onPick={pick} printing={printing} />
                            </ChartCard>
                        </Grid>
                        <Grid item xs={12} lg={6} sx={PRINT_FULL}>
                            <ChartCard title="Reasoning component (RC) by speaker" subtitle="Share of each speaker’s utterances">
                                <RcChart data={stats.rc} onPick={pick} printing={printing} />
                                <Box sx={{ mt: 1.5 }}>
                                    <Legend items={RC_ORDER.map((c) => ({ color: RC_COLORS[c], label: `${c} ${RC_NAMES[c]}` }))} />
                                </Box>
                            </ChartCard>
                        </Grid>
                    </Grid>

                    <ChartCard
                        title="Lesson timeline"
                        subtitle="Reasoning complexity (CCI) of each utterance, with a 10-utterance rolling average"
                        legend={<Legend items={[{ color: SPEAKER_SERIES.teacher, label: 'Teacher' }, { color: SPEAKER_SERIES.student, label: 'Student' }, { color: '#0B1220', label: 'Rolling average', line: true }]} />}
                    >
                        <Timeline data={stats.timeline} printing={printing} />
                    </ChartCard>

                    <Box id="utterances" sx={{ scrollMarginTop: 72 }}>
                        <UtteranceTable utterances={lesson.utterances} filter={filter} setFilter={setFilter} />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default LessonReport;
